import { observable, computed, autorun, extendObservable } from "mobx";
import { Exclude, Expose, serialize, classToPlain } from "class-transformer";
import {
  fileteredDeserialization,
  safeCopy
} from "../../common_components/serialization/common";
import {
  NODE_TYPES,
  EDGE_TYPES,
  MVPS_FEATURES_STATUSES,
  REGULAR_FONT_SIZE,
  CURRENT_MVP_FONT_SIZE
} from "./constants";
import {
  nodeId,
  safeMomentDate,
  getBorderColor,
  getBackgroundColor
} from "./functions";
import moment from "moment";
import NetworkEdges from "./NetworkEdges";

// @Exclude and @Expose are used to exclude all class attributes from
// serialization and expose only a group of them
@Exclude()
export default class VpElementAbstract {
  constructor(store, id, isPersisted = true, nodeType = null) {
    if (new.target === VpElementAbstract) {
      throw new TypeError("Cannot construct AbstractModel instances directly");
    }

    this.store = store;
    this.isPersisted = isPersisted;
    this.nodeType = nodeType;
    extendObservable(this, {
      @Expose()
      name: "",

      @Expose()
      description: "",

      @Expose()
      id: id,

      @Expose()
      igtasksBoardId: "",

      @Expose()
      documentationId: "",

      @Expose()
      priority: "",

      @Expose()
      complexity: "",

      @Expose()
      status: "",

      @Expose()
      users: [],

      startDate: null,

      finishDate: null,

      creationId: null
    });

    this.oldEdgesIds = [];
    this.igtasksBoardPermission = undefined;
  }

  get nodeMappingId() {
    if (this.creationId) {
      return nodeId(this.nodeType, this.creationId);
    }
    return nodeId(this.nodeType, this.id);
  }

  get nodeMapping() {
    const mapping = {
      id: this.nodeMappingId,
      label: this.name,
      title: this.description || this.name,
      idNumber: this.id,
      igtasksBoardId: this.igtasksBoardId,
      group: this.nodeType,
      layer: this.layer,
      color: {
        border: getBorderColor(this.status),
        background: getBackgroundColor(this.status)
      },
      font: {
        size: REGULAR_FONT_SIZE
      },
      igtasksBoardPermission: this.igtasksBoardPermission
    };
    if (this.hasBmc) {
      mapping["bmcBoardId"] = this.bmcBoardId;
    }
    return mapping;
  }

  get timelineNodeMapping() {
    if (!this.startDate || !moment(this.startDate).isValid()) {
      return null;
    }
    const mapping = {
      className: this.nodeType,
      id: this.nodeMappingId,
      title: this.humanizedStartDate + "<br>" + this.humanizedFinishDate,
      nodeType: this.nodeType,
      idNumber: this.id,
      group: this.timelineGroup,
      layer: this.layer,
      content: this.myName || this.name,
      start: moment(this.startDate).format(),
      end: this.finishDate != "" ? moment(this.finishDate).format() : null,
      style:
        "border-color:" +
        getBorderColor(this.status) +
        ";background-color:" +
        getBorderColor(this.status) +
        ";"
    };
    return mapping;
  }

  get timelineGroup() {
    if (this.nodeType == NODE_TYPES.SECTION_FEATURE) {
      return this.nodeType + "_" + this.section;
    }
    return this.nodeType;
  }

  get humanizedStartDate() {
    if (this.startDate != "") {
      return moment(this.startDate).format("dddd, LL");
    }
    return "";
  }

  get humanizedFinishDate() {
    if (this.finishDate != "") {
      return moment(this.finishDate).format("dddd, LL");
    }
    return "";
  }

  get isNew() {
    return this.store == null;
  }

  serialize() {
    let basicSerialize = classToPlain(this);
    basicSerialize["startDate"] = this.startDate;
    basicSerialize["finishDate"] = this.finishDate;
    return basicSerialize;
  }

  update(source) {
    this.updateWithoutPersistance(source);
    this.persist();
  }

  persist() {
    this.store.update(this);
  }

  updateWithoutPersistance(source) {
    this.setOldEdgesIds();
    safeCopy(this, source);
    if (source.functional) {
      this.status = MVPS_FEATURES_STATUSES.FUNCTIONAL;
    } else if (source.definition) {
      this.status = MVPS_FEATURES_STATUSES.DEFINITION;
    } else {
      this.status = MVPS_FEATURES_STATUSES.TO_START;
    }
    this.updateStatus();
    this.setUsersFromMembersFormSelect(source.members);
  }

  setUsersFromMembersFormSelect(members) {
    this.users = members
      .map(m => this.store.rootStore.getMember(m.value))
      .filter(Boolean); // This filter removes all 'undefined' elements in the array
    // so that only valid members are considered
  }

  setOldEdgesIds() {
    this.oldEdgesIds = this.edgesMapping.map(edge => edge.id);
  }

  updateDates(startDate, finishDate) {
    if (startDate) {
      this.startDate = moment(startDate);
    }
    if (finishDate) {
      this.finishDate = moment(finishDate);
    }
    this.updateStatus();
    this.persist();
  }

  updateStatus() {
    if (
      this.status == MVPS_FEATURES_STATUSES.FUNCTIONAL ||
      this.status == MVPS_FEATURES_STATUSES.DEFINITION
    ) {
      return;
    }
    if (!this.startDate && !this.finishDate) {
      this.status = MVPS_FEATURES_STATUSES.TO_START;
    } else {
      if (this.startDate <= moment()) {
        this.status = MVPS_FEATURES_STATUSES.IN_PROGRESS;
      }
      if (this.startDate > moment()) {
        this.status = MVPS_FEATURES_STATUSES.TO_START;
      } else {
        if (this.isCriticalWeek()) {
          this.status = MVPS_FEATURES_STATUSES.CRITICAL_WEEK;
        }
        if (this.isDelayed()) {
          this.status = MVPS_FEATURES_STATUSES.DELAYED;
        }
      }
    }
  }

  isCriticalWeek() {
    return this.finishDate <= moment().add(7, "days");
  }

  isDelayed() {
    return this.finishDate < moment();
  }

  @Expose()
  functional() {
    return (
      this.status == MVPS_FEATURES_STATUSES.FUNCTIONAL ||
      this.status == MVPS_FEATURES_STATUSES.FINISHED
    );
  }

  @Expose()
  definition() {
    return this.status == MVPS_FEATURES_STATUSES.DEFINITION;
  }

  @Expose()
  members() {
    return this.users
      .filter(u => u != undefined)
      .map(u => {
        return { value: u.id, label: u.name };
      });
  }

  @Expose()
  userIds() {
    return this.users.map(u => u.id);
  }

  destroy() {
    this.store.destroy(this);
  }
}

VpElementAbstract.TEXT_PROPS_LENGTH = {
  name: { min: 1, max: 25 },
  description: { min: 0, max: 280 }
};
